import useTempChatStore from "@/store/tempChatStore";
import { getSession } from "@/lib/supabase";
import { handleSessionExpiredError } from "@/lib/utils/auth";

// Default model for temp chats
const DEFAULT_TEMP_CHAT_MODEL = "gpt-4.1-mini";
const DEFAULT_TEMP_CHAT_PROVIDER = "openai";
const DEFAULT_SYSTEM_PROMPT =
  "You are a helpful AI assistant. Provide clear, concise, and helpful responses. This is a temporary chat session.";

// Get backend API base URL from environment or default to localhost
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";

/**
 * Stream a temporary chat conversation via backend API
 * @param {string} userMessage - The user's message
 * @param {string} assistantMessageId - ID of the assistant message to update
 */
export async function streamTempChat(userMessage, assistantMessageId) {
  const store = useTempChatStore.getState();

  // Get selected model and assistant from store
  const {
    selectedModel,
    selectedProvider,
    selectedAssistant,
  } = store;

  // Use selected values or defaults
  const model = selectedModel || DEFAULT_TEMP_CHAT_MODEL;
  const provider = selectedProvider || DEFAULT_TEMP_CHAT_PROVIDER;
  const systemPrompt = selectedAssistant?.systemPrompt || DEFAULT_SYSTEM_PROMPT;

  // Create abort controller for this request
  const abortController = new AbortController();
  store.setAbortController(abortController);

  try {
    // Get existing messages for context (limit to last 10 to keep it simple)
    const existingMessages = store.messages
      .filter((msg) => msg.role !== "assistant" || msg.content.trim()) // Filter out empty assistant messages
      .slice(-10) // Keep only last 10 messages for context
      .map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

    // Prepare messages for API with dynamic system prompt
    const messages = [
      {
        role: "system",
        content: systemPrompt,
      },
      ...existingMessages,
    ];

    // Get user ID from session for token tracking
    let userId = null;
    try {
      const session = await getSession();
      userId = session?.user?.id || null;
    } catch {
      // Silently fail - token tracking will work without user ID
    }

    // Make request to backend streaming endpoint
    const response = await fetch(`${API_BASE_URL}/api/ai/chat/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        provider,
        model,
        messages,
        temperature: 0.7,
        max_tokens: 1000,
        tracking: userId ? { userId } : undefined, // Include user ID for token tracking
      }),
      signal: abortController.signal,
    });

    if (!response.ok) {
      // Handle 401 Unauthorized - session expired
      if (handleSessionExpiredError(response, "TempChatService")) {
        throw new Error('Session expired');
      }

      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `HTTP ${response.status}: ${response.statusText}`
      );
    }

    // Handle Server-Sent Events (SSE) streaming response
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let accumulatedContent = "";

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        // Streaming complete
        store.setStreaming(false, null);
        store.setAbortController(null);
        break;
      }

      // Decode the chunk
      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split("\n");

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          const data = line.slice(6);

          if (data === "[DONE]") {
            continue;
          }

          try {
            const parsed = JSON.parse(data);

            // Handle different SSE event types from backend
            if (parsed.type === "chunk" && parsed.chunk) {
              const content = parsed.chunk.choices?.[0]?.delta?.content;

              if (content) {
                accumulatedContent += content;

                // Update the message in store
                store.updateStreamingContent(
                  assistantMessageId,
                  accumulatedContent
                );
              }
            } else if (parsed.type === "complete") {
              // Stream completed successfully
              break;
            } else if (parsed.type === "error") {
              throw new Error(parsed.error || "Streaming error occurred");
            }
          } catch (parseError) {
            console.warn("Failed to parse streaming data:", parseError);
            // Continue processing other lines
          }
        }
      }
    }
  } catch (error) {
    store.setAbortController(null);

    if (error.name === "AbortError") {
      store.setStreaming(false, null);
      return;
    }

    console.error("Error in temp chat streaming:", error);

    // Don't update message here - let the store's retry logic handle error display
    // The store will show the error as an assistant message after retries are exhausted
    store.setStreaming(false, null);

    throw error;
  }
}

/**
 * Abort any ongoing temp chat stream
 */
export function abortTempChatStream() {
  const store = useTempChatStore.getState();
  return store.abortStream();
}

/**
 * Check if backend API is available
 * @returns {Promise<{available: boolean, errorType: string|null, statusCode?: number, error?: string}>}
 */
export async function isApiKeyAvailable() {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    const response = await fetch(
      `${API_BASE_URL}/api/ai/providers/openai/status`,
      { signal: controller.signal }
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Distinguish between auth (401/403) and server errors (5xx)
      const isAuthError = response.status === 401 || response.status === 403;
      return {
        available: false,
        errorType: isAuthError ? "authentication" : "network",
        statusCode: response.status,
      };
    }

    const result = await response.json();
    return {
      available: result.success && result.available,
      errorType: result.available ? null : "authentication",
    };
  } catch (error) {
    console.error("Error checking API availability:", error);
    // Network error (ECONNREFUSED, ETIMEDOUT, AbortError, etc.)
    return {
      available: false,
      errorType: "network",
      error: error.message,
    };
  }
}

/**
 * Get the model being used for temp chats
 */
export function getTempChatModel() {
  const store = useTempChatStore.getState();
  return store.selectedModel || DEFAULT_TEMP_CHAT_MODEL;
}

/**
 * Get backend API base URL
 */
export function getApiBaseUrl() {
  return API_BASE_URL;
}
